import {
  MapPin,
  Clock,
  Gauge,
  Droplet,
  DollarSign,
  Edit,
  Trash2,
  Fuel,
} from 'lucide-react'

import { formattedDateByLang } from 'src/helpers/formatDateByLang'
import { AlertFuelUpdateWindow } from 'src/features/AlertFuelUpdateWindow'
import { TooltipWrapper } from 'src/components/Tooltip'
import AlertDialogDemo from 'src/components/CustomAlertDialog'

import { useFuelHistoryCard } from 'src/features/FuelHistoryCard/useFuelHistoryCard'
import { Card, CardContent } from 'src/components/shadcn/card'
import { FullFuelTrackingProps } from 'src/api/FuelTracking/types'

export const FuelHistoryCard = ({
  fuelQuantity,
  address,
  dateOfRefuel,
  dateOFLastEdit,
  pricePerLiter,
  odometer,
  totalCost,
  fuelType,
  id,
  currency,
  distanceUnit,
}: FullFuelTrackingProps) => {
  const {
    language,
    localeFields,
    locale,
    handleDelete,
    isEditMode,
    toggleEditMode,
    fuelDictionary,
    metricQuantity,
  } = useFuelHistoryCard(fuelType)

  return (
    <Card className="group w-full p-4 border rounded-[10px] shadow-sm cursor-pointer transition transform hover:shadow-lg hover:-translate-y-1">
      <CardContent className="flex flex-col space-y-3 relative">
        <div className="absolute top-2 right-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <AlertFuelUpdateWindow
            itemId={id}
            isOpen={isEditMode}
            toggle={toggleEditMode}
            address={address}
            dateOfRefuel={dateOfRefuel}
            pricePerLiter={pricePerLiter}
            odometer={odometer}
            totalCost={totalCost}
            fuelQuantity={fuelQuantity}
            fuelType={fuelType}
            currency={currency}
            distanceUnit={distanceUnit}
          />
          <button
            onClick={toggleEditMode}
            className="text-gray-500 hover:text-blue-500"
          >
            <Edit size={16} />
          </button>
          <AlertDialogDemo
            trigger={
              <button className="text-gray-500 hover:text-red-500">
                <Trash2 size={16} />
              </button>
            }
            onConfirm={() => handleDelete(id)}
            accept={locale.alert.accept}
            description={locale.alert.description}
            title={locale.alert.title}
            cancel={locale.alert.cancel}
            isDanger
          />
        </div>
        <div className="flex items-center space-x-2">
          <MapPin className="text-red-500" size={16} />
          <span className="text-lg font-semibold">{address}</span>
        </div>
        <TooltipWrapper
          text={
            dateOFLastEdit
              ? `${locale.dateOfLastEdit} ${formattedDateByLang(
                  dateOFLastEdit.seconds,
                  language,
                )}`
              : locale.noEdited
          }
        >
          <div className="flex items-center text-gray-500 text-sm">
            <Clock className="mr-1" size={16} />
            <span>{formattedDateByLang(dateOfRefuel.seconds, language)}</span>
          </div>
        </TooltipWrapper>
        <div className="border-t pt-2">
          <div className="flex justify-between text-sm">
            <div className="flex items-center space-x-1">
              <Gauge className="text-blue-500" size={16} />
              <span>{localeFields.odometer}:</span>
            </div>
            <span>
              {odometer} ({distanceUnit})
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <div className="flex items-center space-x-1">
              <Droplet className="text-green-500" size={16} />
              <span>{locale.fuelQuantity}:</span>
            </div>
            <span>
              {fuelQuantity} {metricQuantity}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <div className="flex items-center space-x-1">
              <Fuel className="text-red-500" size={16} />
              <span>{locale.fuelType}:</span>
            </div>
            <span>{fuelDictionary[fuelType]}</span>
          </div>
          <div className="flex justify-between text-sm">
            <div className="flex items-center space-x-1">
              <DollarSign className="text-purple-500" size={16} />
              <span>{localeFields.pricePerLiter}:</span>
            </div>
            <span>
              {pricePerLiter} {currency.toUpperCase()}
            </span>
          </div>
          <div className="flex justify-between text-sm font-semibold mt-2">
            <span>{localeFields.totalCost}:</span>
            <span>
              {totalCost} {currency.toUpperCase()}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
